import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import mapboxgl from 'mapbox-gl';
import { toast } from 'react-hot-toast';

export class MapGeocoding {
  constructor(map) {
    this.map = map;
    this.geocoder = null;
    this.marker = null;
    this.pulsingCircle = null; // Ajout de la nouvelle propriété
    this.nigerBounds = [
      [0.16, 11.69], // Sud-ouest
      [15.99, 23.52] // Nord-est
    ];
    // Ajouter une propriété pour l'animation
    this.pulsingDot = {
      width: 100,
      height: 100,
      data: new Uint8Array(100 * 100 * 4),
    };
  }

  initialize(isMobile = false, features = []) {
    this.remove();

    const position = 'top-left';

    this.geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      marker: false,
      placeholder: 'Rechercher un dépôt...',
      collapsed: isMobile,
      clearOnBlur: true,
      minLength: 2,
      showResultsWhileTyping: false,
      flyTo: false, // Désactiver le flyTo automatique
      trackProximity: false, // Désactiver le tracking de proximité
      render: function(item) {
        // Personnaliser l'affichage des résultats
        return `
          <div class="p-2">
            <div class="font-medium">${item.properties.title}</div>
            <div class="text-sm text-gray-500">${item.properties.commune}, ${item.properties.region}</div>
          </div>
        `;
      },
      localGeocoder: (query) => {
        if (query.length < 2) return [];
        const matchingFeatures = features.filter(feature => {
          const title = feature.properties.title || '';
          const adresse = feature.properties.adresse || '';
          const commune = feature.properties.commune || '';
          const searchText = `${title} ${adresse} ${commune}`.toLowerCase();
          return searchText.includes(query.toLowerCase());
        });

        return matchingFeatures.map(feature => ({
          ...feature,
          place_name: `${feature.properties.title} - ${feature.properties.commune}`,
          center: feature.geometry.coordinates,
          place_type: ['depot']
        }));
      },
      localGeocoderOnly: true, // Utiliser uniquement la recherche locale
      flyTo: false // Désactiver l'animation flyTo par défaut
    });

    this.map.addControl(this.geocoder, position);

    // Empêcher la réinitialisation du zoom lors du focus
    const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');
    if (geocoderInput) {
      geocoderInput.addEventListener('focus', (e) => {
        e.stopPropagation();
      });
    }

    const geocoderContainer = document.querySelector('.mapboxgl-ctrl-geocoder');
    if (geocoderContainer) {
      if (isMobile) {
        Object.assign(geocoderContainer.style, {
          position: 'absolute',
          top: '10px',
          left: '10px',
          width: '200px',
          minWidth: '200px',
          maxWidth: '90%',
          zIndex: '1000'
        });

        geocoderContainer.style.removeProperty('bottom');
      } else {
        Object.assign(geocoderContainer.style, {
          position: 'relative',
          width: '250px',
          zIndex: '1'
        });
      }
    }

    const style = document.createElement('style');
    style.textContent = `
      .mapboxgl-ctrl-geocoder {
        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        z-index: 1000 !important;
      }
      
      .mapboxgl-ctrl-geocoder--suggestion-list {
        z-index: 2000 !important;
      }
      
      @media (max-width: 768px) {
        .mapboxgl-ctrl-geocoder {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          width: 200px !important;
          min-width: 200px !important;
          max-width: 90% !important;
          z-index: 1000 !important;
        }

        .mapboxgl-ctrl-geocoder--suggestion-list {
          position: absolute !important;
          top: 100% !important;
          left: 0 !important;
          width: 100% !important;
          max-height: 40vh !important;
          background: white !important;
          border-radius: 4px !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
          z-index: 2000 !important;
        }
      }
    `;

    const oldStyle = document.getElementById('geocoder-style');
    if (oldStyle) {
      oldStyle.remove();
    }

    style.id = 'geocoder-style';
    document.head.appendChild(style);

    if (isMobile) {
      this.geocoder.on('results', () => {
        const suggestionList = document.querySelector('.mapboxgl-ctrl-geocoder--suggestion-list');
        if (suggestionList) {
          suggestionList.classList.add('visible');
        }
      });

      this.geocoder.on('clear', () => {
        const suggestionList = document.querySelector('.mapboxgl-ctrl-geocoder--suggestion-list');
        if (suggestionList) {
          suggestionList.classList.remove('visible');
        }
      });

      document.addEventListener('click', (e) => {
        if (!e.target.closest('.mapboxgl-ctrl-geocoder')) {
          const suggestionList = document.querySelector('.mapboxgl-ctrl-geocoder--suggestion-list');
          if (suggestionList) {
            suggestionList.classList.remove('visible');
          }
        }
      });
    }

    // Gérer le clavier mobile et le zoom
    if (geocoderInput && isMobile) {
      // Gérer le focus
      geocoderInput.addEventListener('focus', (e) => {
        e.preventDefault();
        document.body.style.height = 'calc(100% + 1px)';
        window.scrollTo(0, 0);
      });

      // Gérer la perte de focus
      geocoderInput.addEventListener('blur', () => {
        document.body.style.height = '100%';
        // Fermer le clavier
        geocoderInput.blur();
      });

      // Gérer la sélection d'un résultat
      this.geocoder.on('result', (e) => {
        // Fermer le clavier
        geocoderInput.blur();
        
        // Attendre que le clavier soit fermé avant de zoomer
        setTimeout(() => {
          const coordinates = e.result.geometry.coordinates;
          this.map.easeTo({
            center: coordinates,
            zoom: 16,
            duration: 1500,
            padding: { top: 50, bottom: 200, left: 20, right: 20 }
          });
        }, 300);
      });
    }

    this.setupGeocodeEvents();
  }

  setupGeocodeEvents() {
    this.geocoder.on('result', (e) => {
      const coordinates = e.result.geometry.coordinates;
      const isMobile = window.innerWidth <= 768;

      if (this.marker) {
        this.marker.remove();
        this.marker = null;
      }

      this.addPulsingCircle(coordinates);

      const targetZoom = 16;

      if (isMobile) {
        // Attendre que le clavier soit fermé
        setTimeout(() => {
          this.map.easeTo({
            center: coordinates,
            zoom: targetZoom,
            duration: 1500,
            padding: { top: 50, bottom: 200, left: 20, right: 20 }
          });
          // Forcer la mise à jour de la taille de la carte
          this.map.resize();
        }, 300);
      } else {
        this.map.flyTo({
          center: coordinates,
          zoom: targetZoom,
          duration: 2000
        });
      }

      // Masquer le clavier sur mobile
      if (isMobile) {
        document.activeElement.blur();
      }
    });

    this.geocoder.on('error', () => {
      toast.error('Erreur lors de la recherche');
    });

    this.geocoder.on('clear', () => {
      if (this.marker) {
        this.marker.remove();
        this.marker = null;
      }
      if (this.pulsingCircle) {
        this.map.removeLayer('pulsing-circle');
        this.map.removeSource('point');
        this.pulsingCircle = null;
      }
    });
  }

  addMarker(coordinates) {
    if (this.marker) {
      this.marker.remove();
    }

    this.marker = new mapboxgl.Marker({
      color: '#FF0000'
    })
      .setLngLat(coordinates)
      .addTo(this.map);
  }

  addPulsingCircle(coordinates) {
    if (this.pulsingCircle) {
        this.map.removeImage('pulsing-dot');
        this.map.removeLayer('pulsing-point');
        this.map.removeSource('point');
    }

    const size = 200;
    const pulsingDot = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function() {
            const canvas = document.createElement('canvas');
            canvas.width = this.width;
            canvas.height = this.height;
            this.context = canvas.getContext('2d');
        },
        render: function() {
            const duration = 1000;
            const t = (performance.now() % duration) / duration;
            
            const radius = (size / 2) * 0.3;
            const outerRadius = (size / 2) * 0.7 * t + radius;
            
            const context = this.context;
            context.clearRect(0, 0, this.width, this.height);
            
            context.beginPath();
            context.arc(
                this.width / 2,
                this.height / 2,
                outerRadius,
                0,
                Math.PI * 2
            );
            context.fillStyle = `rgba(255, 0, 0, ${1 - t})`;
            context.fill();
            
            context.beginPath();
            context.arc(
                this.width / 2,
                this.height / 2,
                radius,
                0,
                Math.PI * 2
            );
            context.fillStyle = 'rgba(255, 0, 0, 1)';
            context.strokeStyle = 'white';
            context.lineWidth = 2 + 4 * (1 - t);
            context.fill();
            context.stroke();
            
            this.data = context.getImageData(
                0,
                0,
                this.width,
                this.height
            ).data;
            
            return true;
        }
    };

    this.map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });

    this.map.addSource('point', {
        'type': 'geojson',
        'data': {
            'type': 'Point',
            'coordinates': coordinates
        }
    });

    this.map.addLayer({
        'id': 'pulsing-point',
        'type': 'symbol',
        'source': 'point',
        'layout': {
            'icon-image': 'pulsing-dot',
            'icon-allow-overlap': true
        }
    });

    this.pulsingCircle = true;
}

  isWithinNigerBounds(coordinates) {
    return coordinates[0] >= this.nigerBounds[0][0] &&
           coordinates[0] <= this.nigerBounds[1][0] &&
           coordinates[1] >= this.nigerBounds[0][1] &&
           coordinates[1] <= this.nigerBounds[1][1];
  }

  remove() {
    try {
        if (this.geocoder && this.map) {
            const container = this.geocoder.container;
            if (container && container.parentNode) {
                this.map.removeControl(this.geocoder);
            }
        }
        if (this.pulsingCircle) {
            this.map.removeImage('pulsing-dot');
            this.map.removeLayer('pulsing-point');
            this.map.removeSource('point');
            this.pulsingCircle = null;
        }
    } catch (error) {
        console.warn('Erreur lors de la suppression du geocoder:', error);
    } finally {
        this.geocoder = null;
    }
}

  updateLanguage(lang) {
    if (this.geocoder) {
      this.geocoder.setLanguage(lang);
    }
  }

  setTypes(types) {
    if (this.geocoder) {
      this.geocoder.setTypes(types);
    }
  }

  updateBoundingBox(bbox) {
    if (this.geocoder) {
      this.geocoder.setBbox(bbox);
    }
  }
}